import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Create axios instance with auth header
const axiosInstance = axios.create({
  baseURL: API,
});

// Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      const currentPath = window.location.pathname;
      // Don't redirect if already on login/signup/public pages
      const publicPaths = ['/login', '/signup', '/register', '/', '/welcome', '/start', '/get-quotes', '/pricing', '/about', '/contact', '/how-it-works', '/privacy', '/terms'];
      const isPublicPath = publicPaths.some(path => currentPath === path || currentPath.startsWith('/worker/') || currentPath.startsWith('/job/'));
      
      if (!isPublicPath) {
        console.warn('Session expired, redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Redirect to login
        window.location.href = '/login?expired=true';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => axiosInstance.post('/auth/register', userData),
  login: (credentials) => axiosInstance.post('/auth/login', credentials),
  getMe: () => axiosInstance.get('/auth/me'),
};

// User API
export const userAPI = {
  getMe: () => axiosInstance.get('/auth/me'),
  updateProfile: (profileData) => axiosInstance.put('/users/profile', profileData),
  getSettings: () => axiosInstance.get('/users/settings'),
  updateSettings: (settingsData) => axiosInstance.put('/users/settings', settingsData),
  getPlanLimits: () => axiosInstance.get('/users/plan-limits'),
  getUsage: () => axiosInstance.get('/users/usage'),
};

// Workers API
export const workersAPI = {
  getAll: (params) => axiosInstance.get('/workers', { params }),
  getById: (id) => axiosInstance.get(`/workers/${id}`),
};

// Jobs API
export const jobsAPI = {
  getAll: (params) => axiosInstance.get('/jobs', { params }),
  getById: (id) => axiosInstance.get(`/jobs/${id}`),
  create: (jobData) => axiosInstance.post('/jobs', jobData),
  getMyJobs: (params) => axiosInstance.get('/jobs/my-jobs/list', { params }),
  updateStatus: (jobId, status) => axiosInstance.put(`/jobs/${jobId}/status`, { status }),
};

// Job Templates API
export const jobTemplatesAPI = {
  list: () => axiosInstance.get('/job-templates'),
  create: (data) => axiosInstance.post('/job-templates', data),
  update: (id, data) => axiosInstance.put(`/job-templates/${id}`, data),
  remove: (id) => axiosInstance.delete(`/job-templates/${id}`),
  use: (id) => axiosInstance.post(`/job-templates/${id}/use`),
};

// Favorite Workers API
export const favoriteWorkersAPI = {
  list: () => axiosInstance.get('/favorite-workers'),
  add: (workerId) => axiosInstance.post(`/favorite-workers/${workerId}`),
  remove: (workerId) => axiosInstance.delete(`/favorite-workers/${workerId}`),
  check: (workerId) => axiosInstance.get(`/favorite-workers/check/${workerId}`),
  updateNote: (workerId, note) => axiosInstance.put(`/favorite-workers/${workerId}/note`, { note }),
};

// Scheduled Calls API
export const scheduledCallsAPI = {
  list: (status) => axiosInstance.get('/scheduled-calls', { params: status ? { status } : {} }),
  create: (data) => axiosInstance.post('/scheduled-calls', data),
  confirm: (id, slotIndex) => axiosInstance.post(`/scheduled-calls/${id}/confirm`, { selected_slot_index: slotIndex }),
  reschedule: (id, data) => axiosInstance.post(`/scheduled-calls/${id}/reschedule`, data),
  complete: (id, notes) => axiosInstance.post(`/scheduled-calls/${id}/complete`, { notes }),
  cancel: (id) => axiosInstance.post(`/scheduled-calls/${id}/cancel`),
};

// Applications API
export const applicationsAPI = {
  create: (applicationData) => axiosInstance.post('/applications', applicationData),
  getMyApplications: () => axiosInstance.get('/applications/my-applications'),
  getJobApplications: (jobId) => axiosInstance.get(`/applications/job/${jobId}`),
  updateStatus: (applicationId, status) => axiosInstance.put(`/applications/${applicationId}/status`, { status }),
};

// Projects API
export const projectsAPI = {
  getAll: () => axiosInstance.get('/projects'),
  getById: (id) => axiosInstance.get(`/projects/${id}`),
  update: (id, data) => axiosInstance.put(`/projects/${id}`, data),
};

// Dashboard API
export const dashboardAPI = {
  getContractorDashboard: () => axiosInstance.get('/dashboard/contractor'),
  getWorkerDashboard: () => axiosInstance.get('/dashboard/worker'),
};

// Feedback API
export const feedbackAPI = {
  submit: (feedbackData) => axiosInstance.post('/feedback', feedbackData),
};

// Messages API
export const messagesAPI = {
  getConversations: () => axiosInstance.get('/messages/conversations'),
  getMessages: (partnerId, params) => axiosInstance.get(`/messages/${partnerId}`, { params }),
  sendMessage: (messageData) => axiosInstance.post('/messages', messageData),
  getUnreadCount: () => axiosInstance.get('/messages/unread/count'),
  acceptBooking: (messageId) => axiosInstance.post('/worker-availability/accept-booking', { message_id: messageId }),
  bookingIcs: (messageId) => axiosInstance.get(`/worker-availability/booking-ics/${messageId}`, { responseType: 'blob' }),
  markAsRead: (messageId) => axiosInstance.put(`/messages/${messageId}/read`),
  markAudioPlayed: (messageId) => axiosInstance.put(`/messages/${messageId}/played`),
  searchUsers: (query) => axiosInstance.get('/users/search', { params: { query } }),
  transcribeAudio: (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    return axiosInstance.post('/messages/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  sendAudioMessage: (audioBlob, receiverId, duration) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice_message.webm');
    return axiosInstance.post(`/messages/audio?receiver_id=${receiverId}&duration=${duration}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Payments API
export const paymentsAPI = {
  getPlans: () => axiosInstance.get('/payments/plans'),
  createCheckout: (data) => axiosInstance.post('/payments/checkout', data),
  getStatus: (sessionId) => axiosInstance.get(`/payments/status/${sessionId}`),
  getHistory: () => axiosInstance.get('/payments/history'),
};

// Reviews API
export const reviewsAPI = {
  create: (reviewData) => axiosInstance.post('/reviews', reviewData),
  getUserReviews: (userId, params) => axiosInstance.get(`/reviews/user/${userId}`, { params }),
  getMyReviews: (type) => axiosInstance.get('/reviews/my-reviews', { params: { review_type: type } }),
  canReview: (userId) => axiosInstance.get(`/reviews/can-review/${userId}`),
  pendingPrompts: () => axiosInstance.get('/reviews/pending-prompts'),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axiosInstance.post('/reviews/upload-photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Portfolio API
export const portfolioAPI = {
  getWorkerPortfolio: (workerId) => axiosInstance.get(`/portfolio/${workerId}`),
  addItem: (itemData) => axiosInstance.post('/portfolio', itemData),
  updateItem: (itemId, data) => axiosInstance.put(`/portfolio/${itemId}`, null, { params: data }),
  deleteItem: (itemId) => axiosInstance.delete(`/portfolio/${itemId}`),
};

// Admin API
export const analyticsAPI = {
  summary: (days) => axiosInstance.get('/analytics/summary', { params: { days } }),
  funnels: (days) => axiosInstance.get('/analytics/funnels', { params: { days } }),
};

export const onboardingAPI = {
  checklist: () => axiosInstance.get('/onboarding/checklist'),
};

export const accountAPI = {
  exportData: () => axiosInstance.get('/account/export'),
  deleteAccount: (password) => axiosInstance.post('/account/delete', { password }),
};

export const adminAPI = {
  impersonate: (userId) => axiosInstance.post(`/admin/impersonate/${userId}`),
  getSandboxAccounts: () => axiosInstance.get('/admin/sandbox-accounts'),
  getStats: () => axiosInstance.get('/admin/stats'),
  getUsers: (params) => axiosInstance.get('/admin/users', { params }),
  getUserDetails: (userId) => axiosInstance.get(`/admin/users/${userId}`),
  suspendUser: (userId, suspend, reason) => axiosInstance.put(`/admin/users/${userId}/suspend`, null, { params: { suspend, reason } }),
  verifyUser: (userId, verified) => axiosInstance.put(`/admin/users/${userId}/verify`, null, { params: { verified } }),
  setAdminRole: (userId, promote) => axiosInstance.put(`/admin/users/${userId}/admin-role`, null, { params: { promote } }),
  deleteUser: (userId) => axiosInstance.delete(`/admin/users/${userId}`),
  getJobs: (params) => axiosInstance.get('/admin/jobs', { params }),
  deleteJob: (jobId) => axiosInstance.delete(`/admin/jobs/${jobId}`),
  getReviews: (params) => axiosInstance.get('/admin/reviews', { params }),
  deleteReview: (reviewId) => axiosInstance.delete(`/admin/reviews/${reviewId}`),
  getActivity: (limit) => axiosInstance.get('/admin/activity', { params: { limit } }),
  // Escrow Management
  getEscrows: (params) => axiosInstance.get('/admin/escrows', { params }),
  getEscrowStats: () => axiosInstance.get('/admin/escrows/stats'),
  getEscrowDetails: (escrowId) => axiosInstance.get(`/admin/escrows/${escrowId}`),
  resolveDispute: (escrowId, resolution) => axiosInstance.post(`/admin/escrows/${escrowId}/resolve-dispute`, resolution),
  getDisputes: (params) => axiosInstance.get('/admin/disputes', { params }),
  addEscrowNote: (escrowId, note) => axiosInstance.post(`/admin/escrows/${escrowId}/add-note`, { note }),
  forceReleaseEscrow: (escrowId, note) => axiosInstance.post(`/admin/escrows/${escrowId}/force-release`, { note }),
  cancelEscrow: (escrowId, note) => axiosInstance.post(`/admin/escrows/${escrowId}/cancel`, { note }),
  // Audit Log
  getAuditLogs: (params) => axiosInstance.get('/admin/audit-logs', { params }),
  // Client error reporting
  getClientErrors: (params) => axiosInstance.get('/admin/client-errors', { params }),
  // Payments health (Stripe)
  getPaymentsHealth: () => axiosInstance.get('/wallet/admin/payments-health'),
  setPayoutFee: (percentage) => axiosInstance.put('/wallet/admin/payout-fee', { percentage }),
  getClientErrorGroup: (id) => axiosInstance.get(`/admin/client-errors/${id}`),
  resolveClientError: (id, resolved = true) => axiosInstance.post(`/admin/client-errors/${id}/resolve`, { resolved }),
  deleteClientErrorGroup: (id) => axiosInstance.delete(`/admin/client-errors/${id}`),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: (params) => axiosInstance.get('/notifications', { params }),
  getUnreadCount: () => axiosInstance.get('/notifications/unread-count'),
  markAsRead: (notificationId) => axiosInstance.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => axiosInstance.put('/notifications/read-all'),
  deleteNotification: (notificationId) => axiosInstance.delete(`/notifications/${notificationId}`),
  clearAll: () => axiosInstance.delete('/notifications/clear-all'),
};

// Saved Jobs / Favorites API
export const savedJobsAPI = {
  saveJob: (jobId) => axiosInstance.post(`/jobs/${jobId}/save`),
  unsaveJob: (jobId) => axiosInstance.delete(`/jobs/${jobId}/save`),
  getSavedJobs: (params) => axiosInstance.get('/saved-jobs', { params }),
  checkSavedStatus: (jobId) => axiosInstance.get(`/jobs/${jobId}/saved-status`),
};

// Escrow API
export const escrowAPI = {
  create: (data) => axiosInstance.post('/escrow/create', data),
  fund: (escrowId) => axiosInstance.post(`/escrow/${escrowId}/fund`),
  confirmFunding: (escrowId) => axiosInstance.post(`/escrow/${escrowId}/confirm-funding`),
  release: (escrowId, data) => axiosInstance.post(`/escrow/${escrowId}/release`, data),
  dispute: (escrowId, data) => axiosInstance.post(`/escrow/${escrowId}/dispute`, data),
  respondToDispute: (escrowId, data) => axiosInstance.post(`/escrow/${escrowId}/dispute/respond`, data),
  getDisputeCaseFile: (escrowId) => axiosInstance.get(`/escrow/${escrowId}/dispute`),
  uploadEvidence: (formData) => axiosInstance.post('/escrow/upload-evidence', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getById: (escrowId) => axiosInstance.get(`/escrow/${escrowId}`),
  getAll: (params) => axiosInstance.get('/escrow', { params }),
  addMilestone: (escrowId, data) => axiosInstance.post(`/escrow/${escrowId}/milestone`, data),
  updateMilestoneStatus: (escrowId, milestoneId, data) =>
    axiosInstance.put(`/escrow/${escrowId}/milestone/${milestoneId}/status`, data),
};

// Client Escrow API (homeowner ⇄ contractor)
export const clientEscrowAPI = {
  eligibleClients: () => axiosInstance.get('/client-escrow/eligible-clients'),
  create: (data) => axiosInstance.post('/client-escrow/create', data),
  mine: () => axiosInstance.get('/client-escrow/mine'),
  fund: (id) => axiosInstance.post(`/client-escrow/${id}/fund`),
  confirmFunding: (id) => axiosInstance.post(`/client-escrow/${id}/confirm-funding`),
  release: (id, data) => axiosInstance.post(`/client-escrow/${id}/release`, data),
  dispute: (id, data) => axiosInstance.post(`/client-escrow/${id}/dispute`, data),
  cancel: (id) => axiosInstance.post(`/client-escrow/${id}/cancel`),
};

// Job Alerts API
export const jobAlertsAPI = {
  getPreferences: () => axiosInstance.get('/job-alerts/preferences'),
  savePreferences: (data) => axiosInstance.post('/job-alerts/preferences', data),
  disableAlerts: () => axiosInstance.delete('/job-alerts/preferences'),
  sendTestAlert: () => axiosInstance.get('/job-alerts/test'),
};

// Project Planning API
export const projectPlanningAPI = {
  // Plans
  getPlans: () => axiosInstance.get('/project-plans'),
  getPlanById: (planId) => axiosInstance.get(`/project-plans/${planId}`),
  createPlan: (data) => axiosInstance.post('/project-plans', data),
  updatePlan: (planId, data) => axiosInstance.put(`/project-plans/${planId}`, data),
  deletePlan: (planId) => axiosInstance.delete(`/project-plans/${planId}`),
  getAvailableWorkers: (planId) => axiosInstance.get(`/project-plans/${planId}/available-workers`),
  
  // Tasks
  createTask: (planId, data) => axiosInstance.post(`/project-plans/${planId}/tasks`, data),
  updateTask: (taskId, data) => axiosInstance.put(`/project-tasks/${taskId}`, data),
  updateTaskStatus: (taskId, status) => axiosInstance.patch(`/project-tasks/${taskId}/status?status=${status}`),
  deleteTask: (taskId) => axiosInstance.delete(`/project-tasks/${taskId}`),
  reorderTasks: (taskOrders) => axiosInstance.put('/project-tasks/reorder', { task_orders: taskOrders }),
  assignWorkers: (taskId, workerIds) => axiosInstance.post(`/project-tasks/${taskId}/assign`, { worker_ids: workerIds }),
  unassignWorker: (taskId, workerId) => axiosInstance.delete(`/project-tasks/${taskId}/assign/${workerId}`),
  
  // Milestones
  createMilestone: (planId, data) => axiosInstance.post(`/project-plans/${planId}/milestones`, data),
  toggleMilestone: (milestoneId) => axiosInstance.patch(`/project-milestones/${milestoneId}/toggle`),
  deleteMilestone: (milestoneId) => axiosInstance.delete(`/project-milestones/${milestoneId}`),
  
  // Notes
  createNote: (planId, data) => axiosInstance.post(`/project-plans/${planId}/notes`, data),
  deleteNote: (noteId) => axiosInstance.delete(`/project-notes/${noteId}`),
  toggleNotePin: (noteId) => axiosInstance.patch(`/project-notes/${noteId}/pin`),
};

// Invoice API
export const invoiceAPI = {
  getInvoices: (status) => axiosInstance.get('/invoices', { params: { status } }),
  getInvoice: (invoiceId) => axiosInstance.get(`/invoices/${invoiceId}`),
  createInvoice: (data) => axiosInstance.post('/invoices', data),
  updateInvoice: (invoiceId, data) => axiosInstance.put(`/invoices/${invoiceId}`, data),
  sendInvoice: (invoiceId) => axiosInstance.post(`/invoices/${invoiceId}/send`),
  markPaid: (invoiceId, amount) => axiosInstance.post(`/invoices/${invoiceId}/mark-paid`, null, { params: { amount } }),
  deleteInvoice: (invoiceId) => axiosInstance.delete(`/invoices/${invoiceId}`),
  downloadPDF: (invoiceId) => axiosInstance.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  // Payment
  createPaymentSession: (invoiceId) => axiosInstance.post(`/invoices/${invoiceId}/pay`),
  // Templates
  getTemplates: () => axiosInstance.get('/invoices/templates'),
  getTemplate: (templateId) => axiosInstance.get(`/invoices/templates/${templateId}`),
  createTemplate: (data) => axiosInstance.post('/invoices/templates', data),
  updateTemplate: (templateId, data) => axiosInstance.put(`/invoices/templates/${templateId}`, data),
  deleteTemplate: (templateId) => axiosInstance.delete(`/invoices/templates/${templateId}`),
  createFromTemplate: (templateId, contractorId, jobId, jobTitle) => 
    axiosInstance.post(`/invoices/from-template/${templateId}`, null, { 
      params: { contractor_id: contractorId, job_id: jobId, job_title: jobTitle } 
    }),
  // Recurring Invoices
  getRecurringInvoices: () => axiosInstance.get('/invoices/recurring'),
  getRecurringInvoice: (id) => axiosInstance.get(`/invoices/recurring/${id}`),
  createRecurringInvoice: (data) => axiosInstance.post('/invoices/recurring', data),
  updateRecurringInvoice: (id, data) => axiosInstance.put(`/invoices/recurring/${id}`, data),
  deleteRecurringInvoice: (id) => axiosInstance.delete(`/invoices/recurring/${id}`),
  generateRecurringNow: (id) => axiosInstance.post(`/invoices/recurring/${id}/generate`),
};

// Calendar API
export const calendarAPI = {
  getEvents: (start, end) => axiosInstance.get('/calendar/events', { params: { start, end } }),
  createEvent: (eventData) => axiosInstance.post('/calendar/events', eventData),
  updateEvent: (eventId, eventData) => axiosInstance.put(`/calendar/events/${eventId}`, eventData),
  deleteEvent: (eventId) => axiosInstance.delete(`/calendar/events/${eventId}`),
  getUpcoming: (limit) => axiosInstance.get('/calendar/upcoming', { params: { limit } }),
  syncJob: (jobId) => axiosInstance.post(`/calendar/sync/job/${jobId}`),
  syncEscrow: (escrowId) => axiosInstance.post(`/calendar/sync/escrow/${escrowId}`),
  syncAll: () => axiosInstance.post('/calendar/sync/all'),
  // Reminder settings
  getReminderSettings: () => axiosInstance.get('/calendar/reminder-settings'),
  updateReminderSettings: (settings) => axiosInstance.put('/calendar/reminder-settings', settings),
  sendTestReminder: () => axiosInstance.post('/calendar/test-reminder'),
};

// Reports API
export const reportsAPI = {
  exportEarnings: (format, startDate, endDate) => 
    axiosInstance.get('/reports/earnings', { 
      params: { format, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    }),
  exportJobs: (format, status, startDate, endDate) => 
    axiosInstance.get('/reports/jobs', { 
      params: { format, status, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    }),
  exportPayments: (format, startDate, endDate) => 
    axiosInstance.get('/reports/payments', { 
      params: { format, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    }),
  exportEscrow: (format, status) => 
    axiosInstance.get('/reports/escrow', { 
      params: { format, status },
      responseType: 'blob'
    }),
  exportHiring: (format, startDate, endDate) => 
    axiosInstance.get('/reports/hiring', { 
      params: { format, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    }),
  exportApplications: (format, status, startDate, endDate) => 
    axiosInstance.get('/reports/applications', { 
      params: { format, status, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    }),
  exportWorkHistory: (format, startDate, endDate) => 
    axiosInstance.get('/reports/work-history', { 
      params: { format, start_date: startDate, end_date: endDate },
      responseType: 'blob'
    }),
};

// Two-Factor Authentication API
export const twoFactorAPI = {
  getStatus: () => axiosInstance.get('/2fa/status'),
  setup: () => axiosInstance.post('/2fa/setup'),
  enable: (verificationCode) => axiosInstance.post('/2fa/enable', { verification_code: verificationCode }),
  verify: (code) => axiosInstance.post('/2fa/verify', { code }),
  disable: (password, code) => axiosInstance.post('/2fa/disable', { password, code }),
  getBackupCodes: () => axiosInstance.get('/2fa/backup-codes'),
  regenerateBackupCodes: (code) => axiosInstance.post('/2fa/regenerate-backup-codes', { code }),
};

// Push Notifications API
export const pushAPI = {
  getVapidKey: () => axiosInstance.get('/push/vapid-public-key'),
  subscribe: (subscription) => axiosInstance.post('/push/subscribe', subscription),
  unsubscribe: (subscription) => axiosInstance.post('/push/unsubscribe', subscription),
  getPreferences: () => axiosInstance.get('/push/preferences'),
  updatePreferences: (preferences) => axiosInstance.put('/push/preferences', preferences),
  getStatus: () => axiosInstance.get('/push/status'),
  sendTest: () => axiosInstance.post('/push/test'),
};

// Saved Map Searches API
export const savedSearchesAPI = {
  getAll: () => axiosInstance.get('/saved-searches'),
  getDefault: () => axiosInstance.get('/saved-searches/default'),
  create: (data) => axiosInstance.post('/saved-searches', data),
  update: (id, data) => axiosInstance.put(`/saved-searches/${id}`, data),
  delete: (id) => axiosInstance.delete(`/saved-searches/${id}`),
  setDefault: (id) => axiosInstance.post(`/saved-searches/${id}/set-default`),
  clearDefault: (id) => axiosInstance.post(`/saved-searches/${id}/clear-default`),
};

// Job Matching API
export const matchingAPI = {
  getWorkersForJob: (jobId, params = {}) => axiosInstance.get(`/matching/workers-for-job/${jobId}`, { params }),
  getJobsForWorker: (params = {}) => axiosInstance.get('/matching/jobs-for-worker', { params }),
  calculateScore: (jobId, workerId) => axiosInstance.get('/matching/score', { params: { job_id: jobId, worker_id: workerId } }),
  getTopMatches: () => axiosInstance.get('/matching/top-matches'),
};

// Team Management API
export const teamsAPI = {
  // Team CRUD
  getMyTeam: () => axiosInstance.get('/teams'),
  createTeam: (data) => axiosInstance.post('/teams', data),
  updateTeam: (teamId, data) => axiosInstance.put(`/teams/${teamId}`, data),
  deleteTeam: (teamId) => axiosInstance.delete(`/teams/${teamId}`),
  getTeamStats: (teamId) => axiosInstance.get(`/teams/${teamId}/stats`),
  
  // Members
  getMembers: (teamId) => axiosInstance.get(`/teams/${teamId}/members`),
  inviteMember: (teamId, data) => axiosInstance.post(`/teams/${teamId}/invite`, data),
  removeMember: (teamId, memberId) => axiosInstance.delete(`/teams/${teamId}/members/${memberId}`),
  updateMemberRole: (teamId, memberId, role) => axiosInstance.put(`/teams/${teamId}/members/${memberId}/role`, null, { params: { role } }),
  
  // Invitations
  getMyInvitations: () => axiosInstance.get('/teams/invitations'),
  acceptInvitation: (invitationId) => axiosInstance.post(`/teams/invitations/${invitationId}/accept`),
  declineInvitation: (invitationId) => axiosInstance.post(`/teams/invitations/${invitationId}/decline`),
  
  // Job Assignments
  getAssignments: (teamId, params = {}) => axiosInstance.get(`/teams/${teamId}/assignments`, { params }),
  createAssignment: (teamId, data) => axiosInstance.post(`/teams/${teamId}/assignments`, data),
  removeAssignment: (teamId, assignmentId) => axiosInstance.delete(`/teams/${teamId}/assignments/${assignmentId}`),
  
  // Schedule
  getSchedule: (teamId, params = {}) => axiosInstance.get(`/teams/${teamId}/schedule`, { params }),
  addScheduleEntry: (teamId, data) => axiosInstance.post(`/teams/${teamId}/schedule`, data),
  deleteScheduleEntry: (teamId, entryId) => axiosInstance.delete(`/teams/${teamId}/schedule/${entryId}`),
  
  // Team Chat
  getMessages: (teamId, params = {}) => axiosInstance.get(`/teams/${teamId}/messages`, { params }),
  sendMessage: (teamId, data) => axiosInstance.post(`/teams/${teamId}/messages`, data),
};

// Job Photos API
export const jobPhotosAPI = {
  getJobPhotos: (jobId, photoType) => axiosInstance.get(`/job-photos/job/${jobId}`, { params: { photo_type: photoType } }),
  getMilestonePhotos: (milestoneId) => axiosInstance.get(`/job-photos/milestone/${milestoneId}`),
  addPhoto: (data) => axiosInstance.post('/job-photos', data),
  updatePhoto: (photoId, data) => axiosInstance.put(`/job-photos/${photoId}`, data),
  deletePhoto: (photoId) => axiosInstance.delete(`/job-photos/${photoId}`),
  addToPortfolio: (photoId) => axiosInstance.post(`/job-photos/${photoId}/add-to-portfolio`),
  getStats: (jobId) => axiosInstance.get(`/job-photos/stats/${jobId}`),
};

// Job Costs API
export const jobCostsAPI = {
  getCategories: () => axiosInstance.get('/job-costs/categories'),
  getJobCosts: (jobId, category) => axiosInstance.get(`/job-costs/job/${jobId}`, { params: { category } }),
  addCost: (data) => axiosInstance.post('/job-costs', data),
  updateCost: (costId, data) => axiosInstance.put(`/job-costs/${costId}`, data),
  deleteCost: (costId) => axiosInstance.delete(`/job-costs/${costId}`),
  setBudget: (jobId, data) => axiosInstance.post(`/job-costs/job/${jobId}/budget`, data),
  getSummary: (jobId) => axiosInstance.get(`/job-costs/job/${jobId}/summary`),
  getContractorOverview: () => axiosInstance.get('/job-costs/contractor/overview'),
};

// Time Tracking API
export const timeTrackingAPI = {
  // Clock In/Out
  getActiveSession: () => axiosInstance.get('/time-tracking/active'),
  clockIn: (data) => axiosInstance.post('/time-tracking/clock-in', data),
  clockOut: (data, latitude, longitude) => {
    const params = {};
    if (latitude) params.latitude = latitude;
    if (longitude) params.longitude = longitude;
    return axiosInstance.post('/time-tracking/clock-out', data, { params });
  },
  
  // Breaks
  startBreak: (breakType) => axiosInstance.post('/time-tracking/break/start', { break_type: breakType }),
  endBreak: () => axiosInstance.post('/time-tracking/break/end'),
  
  // Time Entries
  getJobEntries: (jobId, params = {}) => axiosInstance.get(`/time-tracking/job/${jobId}`, { params }),
  getMyEntries: (params = {}) => axiosInstance.get('/time-tracking/my-entries', { params }),
  editEntry: (entryId, data) => axiosInstance.put(`/time-tracking/${entryId}`, data),
  generateFromChecklists: (data) => axiosInstance.post('/time-tracking/generate-from-checklists', data),
  getEntryHistory: (entryId) => axiosInstance.get(`/time-tracking/${entryId}/history`),
  
  // Overtime Rules
  getOvertimeRules: (jobId) => axiosInstance.get(`/time-tracking/job/${jobId}/overtime-rules`),
  setOvertimeRules: (jobId, rules) => axiosInstance.post(`/time-tracking/job/${jobId}/overtime-rules`, rules),
  
  // Timesheets
  getTimesheet: (jobId, startDate, endDate, workerId) => 
    axiosInstance.get(`/time-tracking/timesheet/${jobId}`, { 
      params: { start_date: startDate, end_date: endDate, worker_id: workerId } 
    }),
  getWeeklySummary: (weekOffset = 0) => 
    axiosInstance.get('/time-tracking/worker/weekly-summary', { params: { week_offset: weekOffset } }),
  
  // Export
  exportTimesheet: (jobId, startDate, endDate, workerId, format = 'pdf') =>
    axiosInstance.get('/reports/timesheet', {
      params: { job_id: jobId, start_date: startDate, end_date: endDate, worker_id: workerId, format },
      responseType: 'blob'
    }),
};

// Wallet & Payouts API
export const walletAPI = {
  // Balance
  getBalance: () => axiosInstance.get('/wallet/balance'),
  getTransactions: (params = {}) => axiosInstance.get('/wallet/transactions', { params }),
  
  // Stripe Connect
  startStripeOnboarding: (returnUrl, refreshUrl) => 
    axiosInstance.post('/wallet/stripe-connect/onboard', { return_url: returnUrl, refresh_url: refreshUrl }),
  getStripeStatus: () => axiosInstance.get('/wallet/stripe-connect/status'),
  
  // Payouts
  requestStripePayout: (amount, payoutSpeed, notes) => 
    axiosInstance.post('/wallet/payout/stripe', { amount, payout_speed: payoutSpeed, notes }),
  requestManualPayout: (amount, payoutSpeed, manualData) => 
    axiosInstance.post('/wallet/payout/manual', { amount, payout_speed: payoutSpeed, ...manualData }),
  getPayoutHistory: (limit = 20, status = null) => 
    axiosInstance.get('/wallet/payouts', { params: { limit, status } }),
  
  // Admin
  getPendingPayouts: (limit = 50) => axiosInstance.get('/wallet/admin/pending-payouts', { params: { limit } }),
  processPayoutAction: (payoutId, action, adminNotes, transactionReference) =>
    axiosInstance.post(`/wallet/admin/payout/${payoutId}/action`, { 
      action, 
      admin_notes: adminNotes, 
      transaction_reference: transactionReference 
    }),
};

// Homeowner API
export const homeownerAPI = {
  getDashboard: () => axiosInstance.get('/homeowner/dashboard'),
  getMyLeads: (params) => axiosInstance.get('/homeowner/my-leads', { params }),
  getMyJobs: (params) => axiosInstance.get('/homeowner/my-jobs', { params }),
};

export const jobDocumentsAPI = {
  // Get documents for a job
  getDocuments: (jobId, category = null) => 
    axiosInstance.get(`/job-documents/job/${jobId}`, { params: { category } }),
  
  // Get document stats
  getStats: (jobId) => axiosInstance.get(`/job-documents/job/${jobId}/stats`),
  
  // Upload document (use FormData)
  upload: (jobId, formData, params) => 
    axiosInstance.post(`/job-documents/job/${jobId}/upload?${new URLSearchParams(params)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  // Download document
  download: (documentId) => 
    axiosInstance.get(`/job-documents/document/${documentId}/download`, { responseType: 'blob' }),
  
  // View document inline
  view: (documentId) => 
    axiosInstance.get(`/job-documents/document/${documentId}/view`, { responseType: 'blob' }),
  
  // Update document metadata
  update: (documentId, data) => 
    axiosInstance.patch(`/job-documents/document/${documentId}`, data),
  
  // Delete document
  delete: (documentId) => 
    axiosInstance.delete(`/job-documents/document/${documentId}`),
};

// Worker Teams API
export const workerTeamsAPI = {
  // Create a new team
  create: (teamData) => axiosInstance.post('/worker-teams', teamData),
  
  // List teams
  list: (params) => axiosInstance.get('/worker-teams', { params }),
  
  // Get my teams (led + member)
  getMyTeams: () => axiosInstance.get('/worker-teams/my-teams'),
  
  // Browse public teams
  browse: (params) => axiosInstance.get('/worker-teams/browse', { params }),
  
  // Get team by ID
  getById: (teamId) => axiosInstance.get(`/worker-teams/${teamId}`),
  
  // Update team
  update: (teamId, teamData) => axiosInstance.put(`/worker-teams/${teamId}`, teamData),
  
  // Delete team
  delete: (teamId) => axiosInstance.delete(`/worker-teams/${teamId}`),
  
  // Invite worker to team
  inviteWorker: (teamId, data) => axiosInstance.post(`/worker-teams/${teamId}/invite`, data),
  
  // Get pending invites
  getPendingInvites: () => axiosInstance.get('/worker-teams/invites/pending'),
  
  // Accept invite
  acceptInvite: (inviteId) => axiosInstance.post(`/worker-teams/invites/${inviteId}/accept`),
  
  // Decline invite
  declineInvite: (inviteId) => axiosInstance.post(`/worker-teams/invites/${inviteId}/decline`),
  
  // Remove member
  removeMember: (teamId, memberId) => axiosInstance.delete(`/worker-teams/${teamId}/members/${memberId}`),
  
  // Leave team
  leave: (teamId) => axiosInstance.post(`/worker-teams/${teamId}/leave`),
  
  // Apply to job as team
  applyToJob: (teamId, jobId, applicationData) => 
    axiosInstance.post(`/worker-teams/${teamId}/apply/${jobId}`, applicationData),
  
  // Get team's applications
  getApplications: (teamId) => axiosInstance.get(`/worker-teams/${teamId}/applications`),
  
  // Get team applications for a job (contractor)
  getJobTeamApplications: (jobId) => axiosInstance.get(`/worker-teams/applications/job/${jobId}`),
  
  // Accept team application (contractor)
  acceptApplication: (applicationId) => axiosInstance.post(`/worker-teams/applications/${applicationId}/accept`),
  
  // Reject team application (contractor)
  rejectApplication: (applicationId) => axiosInstance.post(`/worker-teams/applications/${applicationId}/reject`),
  
  // Search workers to invite
  searchWorkers: (params) => axiosInstance.get('/worker-teams/search-workers', { params }),
};

// ============================================================================
// E-Signature Documents API
// ============================================================================
export const documentsAPI = {
  list: (params = {}) => axiosInstance.get('/documents', { params }),
  get: (id) => axiosInstance.get(`/documents/${id}`),
  create: (payload) => axiosInstance.post('/documents', payload),
  sign: (id, payload) => axiosInstance.post(`/documents/${id}/sign`, payload),
  decline: (id, reason) => axiosInstance.post(`/documents/${id}/decline`, { reason }),
  cancel: (id) => axiosInstance.post(`/documents/${id}/cancel`),
  downloadPdf: (id) => axiosInstance.get(`/documents/${id}/pdf`, { responseType: 'blob' }),
};

export const schedulingAPI = {
  generate: (payload) => axiosInstance.post('/scheduling/generate', payload),
  list: () => axiosInstance.get('/scheduling'),
  get: (id) => axiosInstance.get(`/scheduling/${id}`),
  suggestions: (id, phaseId) => axiosInstance.get(`/scheduling/${id}/phases/${phaseId}/suggestions`),
  assign: (id, phaseId, workerId) => axiosInstance.post(`/scheduling/${id}/phases/${phaseId}/assign`, { worker_id: workerId }),
  unassign: (id, phaseId, workerId) => axiosInstance.post(`/scheduling/${id}/phases/${phaseId}/unassign`, { worker_id: workerId }),
  slip: (id, phaseId, payload) => axiosInstance.post(`/scheduling/${id}/phases/${phaseId}/slip`, payload),
  setStatus: (id, status) => axiosInstance.put(`/scheduling/${id}/status`, { status }),
  crewCalendar: (weekStart) => axiosInstance.get('/scheduling/crew-calendar', { params: weekStart ? { week_start: weekStart } : {} }),
  remove: (id) => axiosInstance.delete(`/scheduling/${id}`),
};

export default axiosInstance;
